import { api } from '../api/apis';

// Simple API cache for frontend with stale-while-revalidate support
const cache = new Map();

// Different cache durations for different types of data
const CACHE_DURATIONS = {
    streamers: 5 * 60 * 1000,    // 5 minutes - streamers don't change often
    cards: 3 * 60 * 1000,         // 3 minutes - cards update moderately
    challenges: 2 * 60 * 1000,    // 2 minutes - challenges update frequently
    profile: 5 * 60 * 1000,       // 5 minutes - profile rarely changes
    default: 60 * 1000            // 1 minute - default fallback
};

// Determine cache duration based on URL
const getCacheDuration = (url) => {
    if (url.includes('getStreamers')) return CACHE_DURATIONS.streamers;
    if (url.includes('getCards') || url.includes('getTrendingCards')) return CACHE_DURATIONS.cards;
    if (url.includes('broadcast-cards') || url.includes('purchased-cards')) return CACHE_DURATIONS.challenges;
    if (url.includes('showUserInfo') || url.includes('profile')) return CACHE_DURATIONS.profile;
    return CACHE_DURATIONS.default;
};

/**
 * Wrapper around axios that caches GET requests with stale-while-revalidate
 * @param {string} url - The URL to fetch
 * @param {object} config - Axios config
 * @returns {Promise<Response>} - The axios response
 */
export const cachedGet = async (url, config = {}) => {
    // Don't cache if specifically requested
    if (config.noCache) {
        return api.get(url, config);
    }

    // Create a cache key based on URL and params
    const cacheKey = `${url}${JSON.stringify(config.params || {})}`;
    const cached = cache.get(cacheKey);
    const cacheDuration = getCacheDuration(url);
    const now = Date.now();

    // Stale-while-revalidate: Return cached data immediately if available
    // and fetch fresh data in background if cache is stale
    if (cached) {
        const age = now - cached.timestamp;
        const isStale = age >= cacheDuration;

        if (!isStale) {
            // Cache is fresh - return immediately
            console.log(`[Frontend Cache HIT - Fresh] ${url} (age: ${Math.round(age / 1000)}s)`);
            return {
                data: JSON.parse(JSON.stringify(cached.data)), // Deep copy
                status: 200,
                statusText: 'OK',
                headers: cached.headers,
                config: config,
                request: {},
                fromCache: true
            };
        } else {
            // Cache is stale - return stale data immediately and revalidate in background
            console.log(`[Frontend Cache HIT - Stale] ${url} (age: ${Math.round(age / 1000)}s) - revalidating...`);

            // Return stale data immediately
            const staleResponse = {
                data: JSON.parse(JSON.stringify(cached.data)),
                status: 200,
                statusText: 'OK',
                headers: cached.headers,
                config: config,
                request: {},
                fromCache: true,
                stale: true
            };

            // Revalidate in background (don't await)
            api.get(url, config)
                .then(response => {
                    if (response.status === 200) {
                        cache.set(cacheKey, {
                            data: response.data,
                            headers: response.headers,
                            timestamp: Date.now()
                        });
                        console.log(`[Frontend Cache REVALIDATED] ${url}`);
                    }
                })
                .catch(error => {
                    console.warn(`[Frontend Cache REVALIDATION FAILED] ${url}:`, error.message);
                });

            return staleResponse;
        }
    }

    // No cache - fetch fresh data
    console.log(`[Frontend Cache MISS] ${url}`);
    try {
        const response = await api.get(url, config);

        // Only cache successful responses
        if (response.status === 200) {
            cache.set(cacheKey, {
                data: response.data,
                headers: response.headers,
                timestamp: Date.now()
            });
            console.log(`[Frontend Cache STORED] ${url} (duration: ${cacheDuration / 1000}s)`);
        }

        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Clear the entire cache or a specific key
 * @param {string} [key] - Optional key to clear
 */
export const clearApiCache = (key) => {
    if (key) {
        cache.delete(key);
    } else {
        cache.clear();
    }
};
